import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import clsx from 'clsx';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { TableVirtuoso, TableVirtuosoHandle } from 'react-virtuoso';
import { useAppContext } from 'src/contexts/AppContext';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';

import { itemSizeDefault } from '../Table/Table.utils';
import { DeferredRowContent } from './components/DeferredRowContent';
import { GroupHeaderRow } from './components/GroupHeaderRow';
import * as styles from './components/Table.css';
import { TableProvider } from './components/TableContext';
import { TablePagination } from './components/TablePagination';
import { VirtuosoTable } from './components/VirtuosoTable';
import { VirtuosoTableContext } from './components/VirtuosoTableContext';
import { VirtuosoTableFooter } from './components/VirtuosoTableFooter';
import { VirtuosoTableHead } from './components/VirtuosoTableHead';
import { VirtuosoTableRow } from './components/VirtuosoTableRow';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  RowWrapperV2,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

// Overscan ensures items are pre-rendered before coming into view during fast scrolling
// The throttled event handlers and RAF optimization prevent jank without needing to reduce overscan
const virtuosoTableStyle = { width: '100%', height: '100%' };
const OverscanLength = window.innerHeight / 2;
const DefaultItemHeight = 36;

const memoizedVirtuosoComponents = {
  Table: VirtuosoTable,
  TableHead: VirtuosoTableHead,
  TableRow: VirtuosoTableRow,
  TableFoot: VirtuosoTableFooter,
};

const TableContainerWidth = '--table-container-width';

interface GroupedVirtuosoTableV2Props<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string | null | undefined;
  getItemId?: (data: T) => string | undefined;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void;
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<T>) => boolean);
  activeRowIds?: Set<string>;
  onRowSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  // use rowWrapperV2 for better performance
  rowWrapper?: RowWrapper<T>;
  rowWrapperV2?: RowWrapperV2<T>;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  renderGroupAsNormalRow?: boolean;
  getSubRows?: (row: T) => T[];
  isGroupRow?: (data: T) => boolean;
  processRows?: (rows: Row<T>[], isGroupRow?: (data: T) => boolean) => Row<T>[]; // Optional custom row processing
  /** Row ID to scroll into view after data loads (e.g. after a new group is created). */
  scrollToItemRowId?: string;
  expandOnRowClick?: boolean;
}

export const GroupedVirtuosoTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getRowId,
  getItemId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  onFilteredRowsChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  activeRowIds,
  onRowSelect,
  pagination,
  className,
  summaryData,
  state,
  sortingFns,
  meta,
  style,
  footerCta,
  rowWrapper,
  rowWrapperV2,
  renderGroupHeader,
  renderGroupAsNormalRow,
  getSubRows,
  isGroupRow,
  processRows,
  scrollToItemRowId,
  expandOnRowClick,
}: GroupedVirtuosoTableV2Props<T>) => {
  const { mainRoute } = useAppContext();
  const inFullPageEventView = isInFullPageEventView(mainRoute);

  const {
    table,
    tableColumns,
    renderHeaderGroups,
    renderRowContent,
    renderFooterRows,
    showCheckbox,
  } = useTableSetup({
    actionColId,
    data,
    columns,
    alwaysShowCheckbox,
    getGroupId,
    getItemId,
    isSelectionDisabled,
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange,
    onSortingChange,
    onFilteredRowsChange,
    enableRowSelection,
    enableMultiRowSelection,
    enableSubRowSelection,
    pagination,
    summaryData,
    state,
    sortingFns,
    meta,
    getSubRows,
    isGroupRow,
    getRowId,
    getRowCanExpand: isGroupRow
      ? (row) => Boolean(isGroupRow(row.original))
      : undefined,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [isScrolledToRight, setIsScrolledToRight] = React.useState(false);

  // Measure container width for group headers
  // Throttled to prevent excessive layout recalculations during resize
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let rafId: number | null = null;

    const resizeObserver = new ResizeObserver((entries) => {
      // Cancel pending frame if still queued
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          element.style.setProperty(TableContainerWidth, `${width}px`);
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // Track overflow for hiding pinned column shadow
  // Throttled to prevent excessive DOM queries during scroll
  useEffect(() => {
    const wrapper = scrollContainerRef.current;
    if (!wrapper) return;

    // Find the virtuoso scroller element
    const container = wrapper.querySelector(
      '[data-virtuoso-scroller]'
    ) as HTMLElement;
    if (!container) return;

    let scrollRafId: number | null = null;
    let resizeRafId: number | null = null;

    const checkOverflow = () => {
      const overflow = container.scrollWidth > container.clientWidth;
      if (overflow !== hasOverflow) {
        setHasOverflow(overflow);
      }
    };

    const checkScroll = () => {
      // Throttle scroll checks using requestAnimationFrame
      if (scrollRafId !== null) return;

      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const isAtRight = scrollLeft + clientWidth >= scrollWidth - 1;
        if (isAtRight !== isScrolledToRight) {
          setIsScrolledToRight(isAtRight);
        }
      });
    };

    const handleResize = () => {
      // Throttle resize checks using requestAnimationFrame
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
      }

      resizeRafId = requestAnimationFrame(() => {
        resizeRafId = null;
        checkOverflow();
        checkScroll();
      });
    };

    // Wait for virtuoso to mount
    const timer = setTimeout(() => {
      handleResize();
      container.addEventListener('scroll', checkScroll, { passive: true });
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (scrollRafId !== null) {
        cancelAnimationFrame(scrollRafId);
      }
      if (resizeRafId !== null) {
        cancelAnimationFrame(resizeRafId);
      }
      container.removeEventListener('scroll', checkScroll);
    };
  }, [hasOverflow, isScrolledToRight]);

  const rawTableRows = table.getRowModel().rows;
  const columnWidths = useMemo(
    () => table.getVisibleLeafColumns().map((col) => col.getSize()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.getState().columnSizing, table.getVisibleLeafColumns()]
  );
  const allTableRows = useMemo(
    () => (processRows ? processRows(rawTableRows, isGroupRow) : rawTableRows),
    [rawTableRows, processRows, isGroupRow]
  );

  // Scroll to a specific row when scrollToItemRowId changes (e.g. after new group creation).
  const scrolledRowIdRef = useRef<string | undefined>(undefined);
  const scrollToRowIndex = useMemo(
    () =>
      scrollToItemRowId
        ? allTableRows.findIndex((r) => r.id === scrollToItemRowId)
        : -1,
    [allTableRows, scrollToItemRowId]
  );
  useEffect(() => {
    if (!scrollToItemRowId || scrolledRowIdRef.current === scrollToItemRowId) {
      return;
    }
    if (scrollToRowIndex !== -1 && virtuosoRef.current) {
      scrolledRowIdRef.current = scrollToItemRowId;
      virtuosoRef.current.scrollToIndex({
        index: scrollToRowIndex,
        behavior: 'auto',
        align: 'start',
      });
    }
  }, [scrollToItemRowId, scrollToRowIndex]);

  // When expanding a parent group, also expand its expandable sub-rows
  // (e.g., Active/Inactive sub-groups) so nested listings are visible.
  const handleToggleGroupExpanded = useCallback((row: Row<T>) => {
    startTransition(() => {
      if (row.getIsExpanded()) {
        row.toggleExpanded(false);
      } else {
        row.toggleExpanded(true);
        row.subRows?.forEach((subRow) => {
          if (subRow.getCanExpand() && !subRow.getIsExpanded()) {
            subRow.toggleExpanded(true);
          }
        });
      }
    });
  }, []);

  const renderRowContentWithGrouping = useCallback(
    (index: number, row: Row<T>) => {
      const isParentRow = row.depth === 0 && isGroupRow?.(row.original);

      if (isParentRow && !renderGroupAsNormalRow) {
        // Group headers render immediately (they're lightweight)
        return (
          <GroupHeaderRow
            groupId={getItemId?.(row.original) ?? row.id}
            childData={row.subRows?.map((r) => r.original) || []}
            isExpanded={row.getIsExpanded()}
            toggleExpanded={() => handleToggleGroupExpanded(row)}
            selected={row.getIsSelected() || Boolean(activeRowIds?.has(row.id))}
            renderGroupHeader={renderGroupHeader}
            showCheckbox={showCheckbox}
            parentRow={row}
            table={table}
            getGroupId={getGroupId}
            getItemId={getItemId}
            isSelectionDisabled={isSelectionDisabled}
            tableContainerWidth={TableContainerWidth}
            expandOnRowClick={expandOnRowClick}
          />
        );
      }

      if (rowWrapperV2) {
        return (
          <DeferredRowContent
            columnWidths={columnWidths}
            renderContent={rowWrapperV2(row, index, renderRowContent)}
          />
        );
      }

      const cellContent = renderRowContent(row);
      const wrappedContent = rowWrapper
        ? rowWrapper(row.original, cellContent, index)
        : cellContent;

      return wrappedContent;
    },
    [
      isGroupRow,
      renderGroupAsNormalRow,
      columnWidths,
      renderRowContent,
      rowWrapper,
      getItemId,
      activeRowIds,
      renderGroupHeader,
      showCheckbox,
      table,
      getGroupId,
      isSelectionDisabled,
      handleToggleGroupExpanded,
      rowWrapperV2,
      expandOnRowClick,
    ]
  );

  const virtuosoContext = useMemo<VirtuosoTableContext<T>>(() => {
    return {
      table,
      tableRows: allTableRows,
      variant,
      renderRowContent: renderRowContentWithGrouping,
      activeRowIds,
      onRowSelect,
      isGroupRow,
    };
  }, [
    table,
    allTableRows,
    variant,
    renderRowContentWithGrouping,
    activeRowIds,
    onRowSelect,
    isGroupRow,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: inFullPageEventView ? '100%' : undefined,
        ...(style ?? {}),
      }}
    >
      <TableProvider value={table}>
        <div
          ref={scrollContainerRef}
          className={clsx(
            styles.tableContainer,
            styles.tableBordered,
            styles.POSTableVirtuosoContainer,
            (!hasOverflow || isScrolledToRight) && styles.noOverflow,
            className
          )}
        >
          <TableVirtuoso
            ref={virtuosoRef}
            style={virtuosoTableStyle}
            data={allTableRows}
            overscan={{ main: OverscanLength, reverse: OverscanLength }}
            increaseViewportBy={{ top: OverscanLength, bottom: OverscanLength }}
            defaultItemHeight={DefaultItemHeight}
            totalCount={allTableRows.length}
            context={virtuosoContext}
            components={memoizedVirtuosoComponents}
            fixedHeaderContent={renderHeaderGroups}
            itemContent={renderRowContentWithGrouping}
            itemSize={itemSizeDefault}
            fixedFooterContent={renderFooterRows}
          />
        </div>
      </TableProvider>

      {pagination && (
        <TablePagination
          table={table}
          pageSize={pagination.pageSize}
          variant={pagination.variant}
          totalCount={pagination.totalCount}
          footerCta={footerCta}
        />
      )}
    </div>
  );
};
